/**
 * A aba de Processamento de imagens — o demo comercial (`A-07`).
 *
 * Três colunas, como a Operação SENATRAN: lotes, imagens do lote, lote selecionado.
 *
 * O que esta tela precisa acertar e é fácil errar:
 *
 * **Um arquivo ruim não derruba o lote.** O servidor confere cada arquivo por conta própria
 * e devolve o veredito de cada um; a recusa de um não desfaz os que entraram. A tela mostra
 * a lista de recusados com o motivo ao lado do nome — arrastar vinte fotos, ter duas
 * recusadas e seguir com dezoito é o caminho normal, não a exceção.
 *
 * **O portão de anonimização tem três estados, não dois** (`A-04`). `null` é "ainda não
 * tentamos", `false` é "tentamos e não houve garantia", `true` é "há cópia anônima". A tela
 * diz coisas diferentes para os três, e o download só abre no terceiro. Colapsar os dois
 * primeiros num "bloqueado" faria o operador reprocessar imagem que ninguém tentou
 * anonimizar ainda.
 *
 * **O original nunca é oferecido no lugar da cópia.** Quando o portão está travado, o botão
 * que aparece é o de reprocessar, não o de baixar assim mesmo.
 */
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { DragEvent, FormEvent, ReactNode } from "react";
import { Link } from "react-router";

import { ErroDaApi, api, enviarComProgresso } from "../api/cliente";
import { ContextoDaSessao } from "../api/contexto-sessao";
import { portaoDaImagem } from "../api/tipos";
import type {
  ArquivoProcessado,
  Limpeza,
  ImagemDoLote,
  Limites,
  ResultadoDaCapacidade,
  ResultadoDoEnvio,
  ResumoDoLote,
  CampoCorrigivel,
  CorrecaoFeita,
} from "../api/tipos";
import {
  Badge,
  Botao,
  Cartao,
  Estado,
  Kpi,
  Linha,
  Painel,
} from "../componentes/base";
import { Icone } from "../componentes/icones";
import { dataEHora, hora, tamanhoLegivel } from "../formato";
import { useLote } from "../ganchos/useLote";
import { resumoDaCapacidade } from "../leituras";
import { controlaADemonstracao } from "../componentes/navegacao";

type ChaveDoRecorte =
  "todas" | "fila" | "concluidas" | "rejeitadas" | "travadas";

export function ProcessamentoDeImagens({
  voltarPara,
}: { voltarPara?: string } = {}) {
  const [lotes, definirLotes] = useState<ResumoDoLote[] | null>(null);
  const [erroDosLotes, definirErroDosLotes] = useState<ErroDaApi | null>(null);
  const [selecionado, definirSelecionado] = useState<string | null>(null);
  const [limites, definirLimites] = useState<Limites | null>(null);
  const [recusados, definirRecusados] = useState<ArquivoProcessado[]>([]);
  const [recorte, definirRecorte] = useState<ChaveDoRecorte>("todas");
  const [criando, definirCriando] = useState(false);
  /** Arquivos subindo agora, pela barra de progresso do `Dropzone`. */
  const [carregandoArquivos, definirCarregandoArquivos] = useState(false);
  /** Clicou "Enviar para a fila" e a resposta ainda não voltou. */
  const [enviandoFila, definirEnviandoFila] = useState(false);
  const [erroDoEnvio, definirErroDoEnvio] = useState<string | null>(null);
  const aberto = useLote(selecionado);

  const visiveis = useMemo(
    () => aberto.imagens.filter(RECORTES_DA_IMAGEM[recorte].cabe),
    [aberto.imagens, recorte],
  );

  const carregarLotes = useCallback(async () => {
    try {
      const encontrados = await api<ResumoDoLote[]>("/analise/lotes");
      definirLotes(encontrados);
      definirErroDosLotes(null);
      definirSelecionado((atual) =>
        atual && encontrados.some((l) => l.id === atual)
          ? atual
          : (encontrados[0]?.id ?? null),
      );
    } catch (falha) {
      definirErroDosLotes(
        falha instanceof ErroDaApi ? falha : new ErroDaApi("Falha.", 0),
      );
      definirLotes(null);
    }
  }, []);

  useEffect(() => {
    void carregarLotes();
    void api<Limites>("/analise/limites")
      .then(definirLimites)
      // 402 aqui é o inquilino sem licença do módulo — o painel já diz isso, e repetir na
      // linha dos limites só empilharia a mesma frase.
      .catch(() => definirLimites(null));
  }, [carregarLotes]);

  /**
   * **A tela trava enquanto há um lote em andamento** (Gisele, 21/09): subindo arquivos,
   * mandando para a fila ou com imagem sendo analisada. Novo lote, apagar e enviar voltam
   * quando o procedimento termina — dois lotes ao mesmo tempo na fila de uma demonstração
   * só embaralham o que o cliente está vendo.
   */
  const processando = (lotes ?? []).some((l) => l.enviado_em && l.na_fila > 0);
  const ocupado = carregandoArquivos || enviandoFila || processando;
  const motivoDaTrava = carregandoArquivos
    ? "Aguarde: as imagens ainda estão subindo."
    : enviandoFila || processando
      ? "Aguarde: há um lote sendo processado."
      : undefined;

  // A lista acompanha o lote em processamento: quando o selecionado esvazia a fila, a lista
  // é relida (é ela que destrava a tela) — e, se o lote em andamento é outro, a lista mesma
  // é consultada a cada poucos segundos até ele terminar.
  const naFilaDoAberto = aberto.lote?.na_fila ?? 0;
  const anterior = useRef(naFilaDoAberto);
  useEffect(() => {
    if (anterior.current > 0 && naFilaDoAberto === 0) void carregarLotes();
    anterior.current = naFilaDoAberto;
  }, [naFilaDoAberto, carregarLotes]);
  useEffect(() => {
    if (!processando) return;
    const id = window.setInterval(() => void carregarLotes(), 4000);
    return () => window.clearInterval(id);
  }, [processando, carregarLotes]);

  async function criarLote(nome: string) {
    const novo = await api<ResumoDoLote>("/analise/lotes", {
      method: "POST",
      corpo: { nome },
    });
    definirRecusados([]);
    definirCriando(false);
    definirRecorte("todas");
    definirSelecionado(novo.id);
    await carregarLotes();
  }

  async function renomear(nome: string) {
    if (!aberto.lote) return;
    await api<ResumoDoLote>(`/analise/lotes/${aberto.lote.id}`, {
      method: "PATCH",
      corpo: { nome },
    });
    await Promise.all([aberto.recarregar(), carregarLotes()]);
  }

  // Contexto lido direto, e não por `useSessao`: esta tela também roda em teste sem sessão,
  // e sem sessão o botão simplesmente não aparece.
  const quem = useContext(ContextoDaSessao)?.quem;
  const podeApagar = controlaADemonstracao(quem);

  async function apagar(loteId: string | null): Promise<string> {
    const r = await api<Limpeza>("/demonstracao/limpar-uploads", {
      method: "POST",
      corpo: loteId ? { lote_id: loteId } : {},
    });
    definirRecusados([]);
    if (!loteId || loteId === selecionado) definirSelecionado(null);
    await carregarLotes();
    return loteId
      ? `Lote apagado: ${r.imagens} imagem(ns) e ${r.transitos} registro(s) da Operação.`
      : `Tudo apagado: ${r.lotes} lote(s), ${r.imagens} imagem(ns) e ${r.transitos} registro(s) da Operação.`;
  }

  async function enviarParaFila() {
    if (!aberto.lote || enviandoFila) return;
    definirEnviandoFila(true);
    definirErroDoEnvio(null);
    try {
      await api(`/analise/lotes/${aberto.lote.id}/enviar`, {
        method: "POST",
        corpo: {},
      });
      await Promise.all([aberto.recarregar(), carregarLotes()]);
    } catch (falha) {
      definirErroDoEnvio(
        falha instanceof ErroDaApi
          ? falha.message
          : "Não foi possível enviar para a fila.",
      );
    } finally {
      definirEnviandoFila(false);
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-titulo-tela leading-none font-extrabold tracking-[-0.01em] text-tinta uppercase">
            Processamento de imagens
          </h1>
          <p className="text-subtitulo-tela text-tinta-3">
            Lotes enviados pelo cliente e o que cada IA leu neles
          </p>
        </div>
        {voltarPara && (
          <Link
            to={voltarPara}
            className="inline-flex min-h-8 items-center gap-1.5 rounded-botao border border-linha bg-painel-2 px-2.5 text-[11.5px] font-semibold text-tinta-2 hover:bg-realce"
          >
            ‹ Voltar à fila da Operação
          </Link>
        )}
        {podeApagar && (
          <ApagarUploads
            lotes={lotes ?? []}
            selecionado={aberto.lote}
            {...(motivoDaTrava ? { travado: motivoDaTrava } : {})}
            aoApagar={apagar}
          />
        )}
        <span className="ml-auto inline-flex items-center gap-1.5 rounded-pilula border border-linha bg-painel-2 px-2.5 py-1 text-[11px] text-tinta-3">
          {limites
            ? `${limites.tipos_aceitos.map((t) => t.replace("image/", "").toUpperCase()).join(" · ")} · até ${tamanhoLegivel(limites.tamanho_maximo_bytes)} por imagem`
            : "—"}
        </span>
      </div>

      <ReguaDoLote
        imagens={aberto.imagens}
        recorte={recorte}
        aoRecortar={definirRecorte}
      />

      <div className="grid min-h-0 grid-cols-1 gap-3 xl:grid-cols-[320px_1fr_430px]">
        <Painel
          titulo="Lotes"
          acoes={
            <Botao
              papel="primaria"
              disabled={ocupado || criando}
              title={motivoDaTrava ?? "Abrir um lote novo, com nome"}
              onClick={() => definirCriando(true)}
            >
              Novo lote
            </Botao>
          }
          rodape={<span>{lotes ? `${lotes.length} lote(s)` : "—"}</span>}
        >
          {criando && (
            <FormularioDoLote
              rotulo="Nome do lote"
              acao="Criar lote"
              inicial={nomeSugerido()}
              aoSalvar={criarLote}
              aoCancelar={() => definirCriando(false)}
            />
          )}
          {erroDosLotes ? (
            <Estado
              tipo={erroDosLotes.semLicenca ? "sem-licenca" : "erro"}
              titulo={
                erroDosLotes.semLicenca
                  ? "Esta organização não licenciou o módulo Análise."
                  : "Não foi possível carregar os lotes."
              }
              detalhe={erroDosLotes.message}
            />
          ) : lotes === null ? (
            <Estado tipo="carregando" titulo="Carregando os lotes…" />
          ) : lotes.length === 0 ? (
            !criando && (
              <Estado
                tipo="vazio"
                titulo="Nenhum lote ainda."
                detalhe="Clique em “Novo lote”, dê um nome e arraste as imagens que o cliente mandou."
              />
            )
          ) : (
            <ul aria-label="Lotes" className="grid gap-1.5">
              {lotes.map((l) => (
                <li key={l.id}>
                  <ItemDoLote
                    lote={l}
                    selecionado={l.id === selecionado}
                    aoSelecionar={() => {
                      definirRecusados([]);
                      definirSelecionado(l.id);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </Painel>

        <Painel
          titulo="Imagens do lote"
          acoes={
            <span className="mono text-[11px] text-tinta-3">
              {visiveis.length}
            </span>
          }
          rodape={
            <span>
              Cada arquivo é conferido no servidor; recusa não derruba o lote.
            </span>
          }
        >
          {!aberto.lote ? (
            <Estado
              tipo="vazio"
              titulo="Nenhum lote aberto."
              detalhe="Crie um lote em “Novo lote” para começar."
            />
          ) : (
            <>
              {aberto.lote.enviado_em === null && (
                <Dropzone
                  loteId={aberto.lote.id}
                  bloqueado={enviandoFila}
                  aoMudarOcupado={definirCarregandoArquivos}
                  aoTerminar={async (resultado) => {
                    definirRecusados(
                      resultado.arquivos.filter((a) => !a.aceito),
                    );
                    await Promise.all([aberto.recarregar(), carregarLotes()]);
                  }}
                />
              )}
              <GradeDeImagens
                imagens={visiveis}
                resultados={aberto.resultados}
                aoReprocessar={aberto.recarregar}
                recortado={recorte !== "todas"}
              />
            </>
          )}
        </Painel>

        <Painel
          titulo="Lote selecionado"
          rodape={
            aberto.lote ? (
              <AcoesDoLote
                lote={aberto.lote}
                comOperacao={Boolean(voltarPara)}
                carregandoArquivos={carregandoArquivos}
                enviando={enviandoFila}
                erro={erroDoEnvio}
                aoEnviar={() => void enviarParaFila()}
                aoNovoLote={() => definirCriando(true)}
              />
            ) : undefined
          }
        >
          {!aberto.lote ? (
            <Estado tipo="vazio" titulo="Selecione um lote à esquerda." />
          ) : (
            <>
              <Cartao titulo="Lote" icone={<Icone.Lista />}>
                <NomeDoLote
                  key={aberto.lote.id}
                  nome={aberto.lote.nome}
                  aoRenomear={renomear}
                />
                <Linha rotulo="Aberto em" mono>
                  {hora(aberto.lote.criado_em)}
                </Linha>
                <Linha rotulo="Situação">
                  <Badge cor={COR_DA_SITUACAO[situacaoDoLote(aberto.lote)]}>
                    {ROTULO_DA_SITUACAO[situacaoDoLote(aberto.lote)]}
                  </Badge>
                </Linha>
              </Cartao>

              {recusados.length > 0 && (
                <Cartao titulo="Arquivos recusados" icone={<Icone.Alerta />}>
                  {/* A recusa vem com o nome do arquivo ao lado do motivo, de propósito:
                      "2 arquivos recusados" manda o cliente adivinhar quais. */}
                  {recusados.map((a) => (
                    <Linha key={a.nome} rotulo={a.nome}>
                      <span className="text-vermelho">
                        {a.explicacao ?? a.motivo ?? "recusado"}
                      </span>
                    </Linha>
                  ))}
                </Cartao>
              )}
            </>
          )}
        </Painel>
      </div>
    </>
  );
}

/** "Lote 21/09 19:53" — o nome que já vem escrito, selecionado, para a pessoa trocar. */
function nomeSugerido(agora = new Date()): string {
  const dois = (n: number) => String(n).padStart(2, "0");
  return `Lote ${dois(agora.getDate())}/${dois(agora.getMonth() + 1)} ${dois(agora.getHours())}:${dois(agora.getMinutes())}`;
}

type SituacaoDoLote = "aberto" | "processando" | "processado";

function situacaoDoLote(lote: ResumoDoLote): SituacaoDoLote {
  if (!lote.enviado_em) return "aberto";
  return lote.na_fila > 0 ? "processando" : "processado";
}

const ROTULO_DA_SITUACAO: Record<SituacaoDoLote, string> = {
  aberto: "aberto — recebendo imagens",
  processando: "processando",
  processado: "processado",
};

const COR_DA_SITUACAO = {
  aberto: "cinza",
  processando: "azul",
  processado: "verde",
} as const;

/** Quanto do lote já saiu da fila, de 0 a 100. */
function percentualProcessado(lote: ResumoDoLote): number {
  if (lote.imagens === 0) return 100;
  return Math.round((100 * (lote.imagens - lote.na_fila)) / lote.imagens);
}

/**
 * Uma barra de progresso de verdade: `role="progressbar"` com o valor, para o leitor de tela
 * e para o teste lerem o mesmo número que o olho lê.
 */
function BarraDeProgresso({
  rotulo,
  percentual,
  detalhe,
  indeterminada = false,
}: {
  rotulo: string;
  percentual: number;
  detalhe?: string;
  indeterminada?: boolean;
}) {
  const valor = Math.max(0, Math.min(100, Math.round(percentual)));
  return (
    <div className="grid w-full gap-1 text-left" aria-live="polite">
      <div className="flex items-baseline justify-between gap-2 text-[11.5px]">
        <span className="font-semibold text-tinta">{rotulo}</span>
        <span className="mono text-tinta-2">{valor}%</span>
      </div>
      <div
        role="progressbar"
        aria-label={rotulo}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={valor}
        className="h-2 w-full overflow-hidden rounded-pilula border border-linha bg-painel"
      >
        <div
          className={`h-full rounded-pilula bg-azul-cheio transition-[width] duration-300 ${indeterminada ? "animate-pulse" : ""}`}
          style={{ width: `${valor}%` }}
        />
      </div>
      {detalhe && <span className="text-[10.5px] text-tinta-3">{detalhe}</span>}
    </div>
  );
}

/**
 * O nome do lote, digitado — ao criar e ao renomear. Enter salva, Esc cancela, e o nome
 * sugerido vem selecionado: digitar já substitui.
 */
function FormularioDoLote({
  rotulo,
  acao,
  inicial,
  aoSalvar,
  aoCancelar,
}: {
  rotulo: string;
  acao: string;
  inicial: string;
  aoSalvar: (nome: string) => Promise<void>;
  aoCancelar: () => void;
}) {
  const [nome, definirNome] = useState(inicial);
  const [salvando, definirSalvando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  const campo = useRef<HTMLInputElement>(null);
  useEffect(() => {
    campo.current?.focus();
    campo.current?.select();
  }, []);
  const limpo = nome.trim();

  async function salvar(evento: FormEvent) {
    evento.preventDefault();
    if (!limpo || salvando) return;
    definirSalvando(true);
    definirErro(null);
    try {
      await aoSalvar(limpo);
    } catch (falha) {
      definirErro(
        falha instanceof ErroDaApi ? falha.message : "Não foi possível salvar.",
      );
      definirSalvando(false);
    }
  }

  return (
    <form
      aria-label={rotulo}
      onSubmit={(e) => void salvar(e)}
      onKeyDown={(e) => {
        if (e.key === "Escape") aoCancelar();
      }}
      className="mb-2 grid gap-1.5 rounded-cartao border border-azul/60 bg-realce/40 p-2"
    >
      <label className="grid gap-0.5 text-[10.5px] font-semibold text-tinta-3">
        {rotulo}
        <input
          ref={campo}
          value={nome}
          maxLength={120}
          required
          aria-label={rotulo}
          placeholder="Ex.: Bombeiros Maricá"
          onChange={(e) => definirNome(e.target.value)}
          className="min-h-8 rounded-botao border border-linha bg-painel-2 px-2 text-[12px] text-tinta"
        />
      </label>
      <div className="flex gap-1.5">
        <Botao papel="primaria" type="submit" disabled={!limpo || salvando}>
          {salvando ? "Salvando…" : acao}
        </Botao>
        <Botao onClick={aoCancelar} disabled={salvando}>
          Cancelar
        </Botao>
      </div>
      {erro && (
        <p role="alert" className="text-[10.5px] text-vermelho">
          {erro}
        </p>
      )}
    </form>
  );
}

function NomeDoLote({
  nome,
  aoRenomear,
}: {
  nome: string | null;
  aoRenomear: (nome: string) => Promise<void>;
}) {
  const [editando, definirEditando] = useState(false);
  if (editando) {
    return (
      <FormularioDoLote
        rotulo="Renomear o lote"
        acao="Salvar nome"
        inicial={nome ?? ""}
        aoSalvar={async (novo) => {
          await aoRenomear(novo);
          definirEditando(false);
        }}
        aoCancelar={() => definirEditando(false)}
      />
    );
  }
  return (
    <Linha rotulo="Nome">
      <span className="inline-flex items-center gap-1.5">
        <span className={nome ? "text-tinta" : "text-tinta-3 italic"}>
          {nome ?? "sem nome"}
        </span>
        <button
          type="button"
          onClick={() => definirEditando(true)}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-azul hover:underline"
        >
          <Icone.Lapis />
          renomear
        </button>
      </span>
    </Linha>
  );
}

/**
 * Apagar os uploads da demonstração — **todos ou um lote só** (Gisele, 21/09). Dois passos:
 * escolher o que apagar e confirmar vendo o tamanho do estrago. Travado enquanto um lote
 * está subindo ou sendo processado: apagar o que o worker está lendo não termina bem.
 */
function ApagarUploads({
  lotes,
  selecionado,
  travado,
  aoApagar,
}: {
  lotes: ResumoDoLote[];
  selecionado: ResumoDoLote | null;
  travado?: string;
  aoApagar: (loteId: string | null) => Promise<string>;
}) {
  const [aberto, definirAberto] = useState(false);
  const [escopo, definirEscopo] = useState<"lote" | "tudo">("lote");
  const [apagando, definirApagando] = useState(false);
  const [aviso, definirAviso] = useState<string | null>(null);
  const totalDeImagens = lotes.reduce((s, l) => s + l.imagens, 0);
  const alvo = escopo === "lote" && selecionado ? selecionado : null;

  function abrir() {
    definirAviso(null);
    definirEscopo(selecionado ? "lote" : "tudo");
    definirAberto(true);
  }

  async function confirmar() {
    definirApagando(true);
    try {
      definirAviso(await aoApagar(alvo ? alvo.id : null));
      definirAberto(false);
    } catch (falha) {
      definirAviso(
        falha instanceof ErroDaApi ? falha.message : "Não foi possível apagar.",
      );
    } finally {
      definirApagando(false);
    }
  }

  return (
    <span className="relative flex flex-wrap items-center gap-2">
      <Botao
        papel="secundaria"
        disabled={Boolean(travado) || lotes.length === 0 || aberto}
        title={
          travado ??
          "Demonstração: apaga um lote ou todos, com os registros que nasceram deles na Operação. O resto do banco fica."
        }
        onClick={abrir}
      >
        <Icone.Barrado />
        Apagar imagens…
      </Botao>
      {aviso && !aberto && (
        <span role="status" className="text-[11px] text-tinta-2">
          {aviso}
        </span>
      )}
      {aberto && (
        <div
          role="dialog"
          aria-label="Apagar imagens"
          className="absolute top-full left-0 z-20 mt-1.5 grid w-[340px] gap-2 rounded-cartao border border-linha bg-painel p-3 shadow-xl"
        >
          <fieldset className="grid gap-1.5">
            <legend className="mb-1 text-[10.5px] font-bold tracking-[0.08em] text-tinta-3 uppercase">
              O que apagar
            </legend>
            <label
              className={`flex items-start gap-2 rounded-botao border p-2 text-[12px] ${escopo === "lote" ? "border-azul bg-realce" : "border-linha"} ${selecionado ? "" : "opacity-45"}`}
            >
              <input
                type="radio"
                name="escopo"
                checked={escopo === "lote"}
                disabled={!selecionado}
                onChange={() => definirEscopo("lote")}
              />
              <span>
                <b className="text-tinta">Só o lote selecionado</b>
                <small className="block text-tinta-3">
                  {selecionado
                    ? `“${selecionado.nome ?? "sem nome"}” · ${selecionado.imagens} imagem(ns)`
                    : "nenhum lote selecionado"}
                </small>
              </span>
            </label>
            <label
              className={`flex items-start gap-2 rounded-botao border p-2 text-[12px] ${escopo === "tudo" ? "border-vermelho bg-vermelho-fundo/40" : "border-linha"}`}
            >
              <input
                type="radio"
                name="escopo"
                checked={escopo === "tudo"}
                onChange={() => definirEscopo("tudo")}
              />
              <span>
                <b className="text-tinta">Todos os lotes</b>
                <small className="block text-tinta-3">
                  {lotes.length} lote(s) · {totalDeImagens} imagem(ns)
                </small>
              </span>
            </label>
          </fieldset>
          <p className="text-[10.5px] text-tinta-3">
            Os registros que essas imagens criaram na Operação saem junto. O
            extrato de consumo fica. Não dá para desfazer.
          </p>
          <div className="flex gap-1.5">
            <Botao
              papel="perigo"
              disabled={apagando}
              onClick={() => void confirmar()}
            >
              {apagando
                ? "Apagando…"
                : alvo
                  ? "Apagar este lote"
                  : "Apagar todos os lotes"}
            </Botao>
            <Botao disabled={apagando} onClick={() => definirAberto(false)}>
              Cancelar
            </Botao>
          </div>
          {aviso && (
            <p role="alert" className="text-[10.5px] text-vermelho">
              {aviso}
            </p>
          )}
        </div>
      )}
    </span>
  );
}

/**
 * A régua do lote — e ela **filtra a grade abaixo** (§4.1 do design system).
 *
 * Os números saem da lista de imagens, não dos contadores do resumo do lote, mesmo tendo os
 * dois à mão. Com duas fontes, o cartão "Na fila" e a grade que ele abre divergem no
 * intervalo entre uma resposta e outra — e o cliente descobre isso somando as colunas na
 * frente de alguém. Uma fonte só nunca diverge de si mesma.
 */
const RECORTES_DA_IMAGEM: Record<
  ChaveDoRecorte,
  {
    rotulo: string;
    icone: ReactNode;
    severidade?: "atencao" | "critico";
    cabe: (i: ImagemDoLote) => boolean;
  }
> = {
  todas: {
    rotulo: "Imagens no lote",
    icone: <Icone.Lista />,
    cabe: () => true,
  },
  fila: {
    rotulo: "Na fila",
    icone: <Icone.Relogio />,
    severidade: "atencao",
    cabe: (i) =>
      i.status === "NA_FILA" ||
      i.status === "PROCESSANDO" ||
      i.status === "RECEBIDA",
  },
  concluidas: {
    rotulo: "Concluídas",
    icone: <Icone.Escudo />,
    cabe: (i) => i.status === "CONCLUIDA",
  },
  rejeitadas: {
    rotulo: "Rejeitadas",
    icone: <Icone.Barrado />,
    severidade: "critico",
    cabe: (i) => i.status === "REJEITADA",
  },
  travadas: {
    rotulo: "Download travado",
    icone: <Icone.Alerta />,
    severidade: "critico",
    cabe: (i) => portaoDaImagem(i) === "travado",
  },
};

const ORDEM_DOS_RECORTES: ChaveDoRecorte[] = [
  "todas",
  "fila",
  "concluidas",
  "rejeitadas",
  "travadas",
];

function ReguaDoLote({
  imagens,
  recorte,
  aoRecortar,
}: {
  imagens: ImagemDoLote[];
  recorte: ChaveDoRecorte;
  aoRecortar: (chave: ChaveDoRecorte) => void;
}) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(168px,1fr))] gap-2.5">
      {ORDEM_DOS_RECORTES.map((chave) => {
        const r = RECORTES_DA_IMAGEM[chave];
        return (
          <Kpi
            key={chave}
            rotulo={r.rotulo}
            valor={imagens.filter(r.cabe).length}
            icone={r.icone}
            {...(r.severidade ? { severidade: r.severidade } : {})}
            ativo={recorte === chave}
            aoClicar={() => aoRecortar(chave)}
          />
        );
      })}
    </div>
  );
}

function ItemDoLote({
  lote,
  selecionado,
  aoSelecionar,
}: {
  lote: ResumoDoLote;
  selecionado: boolean;
  aoSelecionar: () => void;
}) {
  const situacao = situacaoDoLote(lote);
  return (
    <button
      type="button"
      aria-current={selecionado}
      onClick={aoSelecionar}
      className={`grid w-full grid-cols-[auto_1fr] items-center gap-2 rounded-[7px] border-l-2 px-2.5 py-2 text-left transition-colors ${
        selecionado
          ? "border-l-azul bg-realce"
          : "border-l-transparent bg-painel-2 hover:bg-realce/60"
      }`}
    >
      <span className="mono grid size-9 place-items-center rounded-botao border border-linha text-tinta-2">
        {lote.imagens}
      </span>
      <span className="min-w-0">
        <b className="block truncate text-tinta">{lote.nome ?? "sem nome"}</b>
        <small className="block text-[11px] text-tinta-3">
          {hora(lote.criado_em)} ·{" "}
          {situacao === "processando"
            ? `processando ${percentualProcessado(lote)}%`
            : ROTULO_DA_SITUACAO[situacao].split(" —")[0]}
        </small>
        {situacao === "processando" && (
          <span className="mt-1 block h-1 overflow-hidden rounded-pilula bg-painel">
            <span
              className="block h-full bg-azul-cheio transition-[width] duration-300"
              style={{ width: `${percentualProcessado(lote)}%` }}
            />
          </span>
        )}
      </span>
    </button>
  );
}

function AcoesDoLote({
  lote,
  comOperacao,
  carregandoArquivos,
  enviando,
  erro,
  aoEnviar,
  aoNovoLote,
}: {
  lote: ResumoDoLote;
  /** A tela está dentro da Operação (TollVision licenciado): há para onde mandar. */
  comOperacao: boolean;
  carregandoArquivos: boolean;
  enviando: boolean;
  erro: string | null;
  aoEnviar: () => void;
  aoNovoLote: () => void;
}) {
  const situacao = situacaoDoLote(lote);
  return (
    <div className="grid w-full gap-2">
      <h4 className="text-[10.5px] font-bold tracking-[0.08em] text-tinta-3 uppercase">
        Ações para este lote
      </h4>
      {situacao === "aberto" ? (
        <>
          {/* Trava no clique, e não na resposta: dois cliques rápidos mandavam o mesmo lote
              duas vezes, e o segundo voltava 409 na cara de quem estava apresentando. */}
          <Botao
            papel="primaria"
            disabled={lote.imagens === 0 || carregandoArquivos || enviando}
            aria-busy={enviando}
            onClick={aoEnviar}
          >
            <Icone.Enviar />
            {enviando ? "Enviando para a fila…" : "Enviar para a fila"}
          </Botao>
          <p className="text-[10.5px] text-tinta-3">
            {carregandoArquivos
              ? "Aguarde as imagens terminarem de subir."
              : lote.imagens === 0
                ? "Um lote sem imagens não vai para a fila."
                : "Depois de enviado, nada mais entra neste lote — é o que separa “o cliente ainda está arrastando arquivos” de “pode processar”."}
          </p>
          {erro && (
            <p role="alert" className="text-[10.5px] text-vermelho">
              {erro}
            </p>
          )}
        </>
      ) : situacao === "processando" ? (
        <>
          <BarraDeProgresso
            rotulo="Processando o lote…"
            percentual={percentualProcessado(lote)}
            detalhe={`${lote.imagens - lote.na_fila} de ${lote.imagens} imagem(ns) analisada(s). Novo lote e apagar voltam quando terminar.`}
          />
          {comOperacao && <VeiculosNaOperacao lote={lote} />}
        </>
      ) : (
        <>
          <p
            role="status"
            className="flex items-center gap-1.5 text-[12px] font-semibold text-verde"
          >
            <Icone.Certo />
            Lote processado: {lote.concluidas} concluída(s)
            {lote.rejeitadas > 0 && `, ${lote.rejeitadas} rejeitada(s)`}.
          </p>
          {comOperacao && <VeiculosNaOperacao lote={lote} />}
          <Botao papel="secundaria" onClick={aoNovoLote}>
            <Icone.Subir />
            Carregar outro lote
          </Botao>
          <p className="text-[10.5px] text-tinta-3">
            As fotos do mesmo veículo (mesma placa ou mesmo nome antes de
            _frente, _lateral, _traseira) viram <b>um</b> registro na Operação.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * A ponte para a Operação depois de enviar (Gisele, 21/09: "fica confuso depois"). Diz
 * quantas fotos viraram quantos veículos e abre a Operação só com eles. O número vem do
 * TollVision (`?lote=`) — a tela compõe; o módulo de Análise não lê o outro.
 */
function VeiculosNaOperacao({ lote }: { lote: ResumoDoLote }) {
  const [veiculos, definirVeiculos] = useState<number | null>(null);
  const [disponivel, definirDisponivel] = useState(true);
  const [semRegistros, definirSemRegistros] = useState(false);
  // Os registros da Operação nascem pelo outbox, segundos depois da última imagem: com o
  // lote pronto e zero veículos, continua perguntando em vez de mostrar "→ 0 veículos".
  const emAndamento = lote.na_fila > 0 || semRegistros;
  useEffect(() => {
    let vivo = true;
    const buscar = async () => {
      try {
        const lista = await api<unknown[]>(
          `/tollvision/transitos?lote=${lote.id}&limite=200`,
        );
        if (vivo) {
          definirVeiculos(lista.length);
          definirSemRegistros(lista.length === 0 && lote.imagens > 0);
        }
      } catch {
        // Sem TollVision licenciado não há Operação para onde ir: o botão some.
        if (vivo) definirDisponivel(false);
      }
    };
    void buscar();
    const id = emAndamento ? window.setInterval(() => void buscar(), 5000) : 0;
    return () => {
      vivo = false;
      if (id) window.clearInterval(id);
    };
  }, [lote.id, lote.imagens, emAndamento]);
  if (!disponivel) return null;
  return (
    <Link
      to={`/operacao?lote=${lote.id}&janela=tudo`}
      className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded-botao border border-verde-cheio bg-verde-cheio px-3 text-[12px] font-semibold text-white hover:bg-verde hover:text-fundo"
    >
      <Icone.Seta />
      Conferir na Operação
      {veiculos !== null &&
        veiculos > 0 &&
        ` — ${lote.imagens} foto${lote.imagens === 1 ? "" : "s"} → ${veiculos} veículo${veiculos === 1 ? "" : "s"}`}
      {lote.na_fila > 0
        ? " (analisando…)"
        : semRegistros
          ? " (criando os registros…)"
          : ""}
    </Link>
  );
}

/**
 * Onde as imagens entram — com o percentual subindo até 100 e **travado enquanto sobe**
 * (Gisele, 21/09). Soltar arquivo no meio de um envio é ignorado, o botão fica desabilitado,
 * e quem está em volta (`aoMudarOcupado`) trava "Enviar para a fila", "Novo lote" e apagar.
 *
 * Duas fases, e a tela diz as duas: os bytes subindo (o percentual) e o servidor conferindo
 * cada arquivo (a barra cheia, pulsando). Sem a segunda, a barra parava em 100% e parecia
 * travada justamente no arquivo mais pesado.
 */
function Dropzone({
  loteId,
  bloqueado = false,
  aoMudarOcupado,
  aoTerminar,
}: {
  loteId: string;
  bloqueado?: boolean;
  aoMudarOcupado?: (ocupado: boolean) => void;
  aoTerminar: (resultado: ResultadoDoEnvio) => Promise<void>;
}) {
  const [sobre, definirSobre] = useState(false);
  const [progresso, definirProgresso] = useState<number | null>(null);
  const [envio, definirEnvio] = useState<{
    quantos: number;
    bytes: number;
  } | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  const [concluido, definirConcluido] = useState<string | null>(null);
  const entrada = useRef<HTMLInputElement>(null);
  const enviando = progresso !== null;
  const travado = enviando || bloqueado;

  const subir = useCallback(
    async (arquivos: FileList | null) => {
      if (!arquivos || arquivos.length === 0 || travado) return;
      // Arquivo oculto não é foto: o Mac deixa `._foto.JPG` e `.DS_Store` ao lado das
      // imagens copiadas de pendrive ou pasta compartilhada, e eles entravam no lote.
      const lista = Array.from(arquivos).filter((a) => !a.name.startsWith("."));
      if (lista.length === 0) return;
      definirEnvio({
        quantos: lista.length,
        bytes: lista.reduce((s, a) => s + a.size, 0),
      });
      definirProgresso(0);
      definirErro(null);
      definirConcluido(null);
      aoMudarOcupado?.(true);
      try {
        const corpo = new FormData();
        for (const arquivo of lista) corpo.append("arquivos", arquivo);
        const resultado = await enviarComProgresso<ResultadoDoEnvio>(
          `/analise/lotes/${loteId}/imagens`,
          corpo,
          (f) => definirProgresso(Math.round(f * 100)),
        );
        await aoTerminar(resultado);
        definirConcluido(
          `${resultado.aceitos} imagem(ns) carregada(s)` +
            (resultado.recusados > 0
              ? `, ${resultado.recusados} recusada(s) — veja o motivo ao lado.`
              : "."),
        );
      } catch (falha) {
        definirErro(
          falha instanceof ErroDaApi ? falha.message : "O envio não completou.",
        );
      } finally {
        definirProgresso(null);
        definirEnvio(null);
        aoMudarOcupado?.(false);
        if (entrada.current) entrada.current.value = "";
      }
    },
    [loteId, aoTerminar, aoMudarOcupado, travado],
  );

  function aoSoltar(evento: DragEvent) {
    evento.preventDefault();
    definirSobre(false);
    void subir(evento.dataTransfer.files);
  }

  return (
    <div
      aria-busy={enviando}
      onDragOver={(e) => {
        e.preventDefault();
        if (!travado) definirSobre(true);
      }}
      onDragLeave={() => definirSobre(false)}
      onDrop={aoSoltar}
      className={`mb-2.5 grid place-items-center gap-2 rounded-cartao border border-dashed p-5 text-center transition-colors ${
        sobre
          ? "border-azul bg-realce text-tinta"
          : "border-linha bg-painel-2 text-tinta-3"
      } ${travado ? "cursor-progress" : ""}`}
    >
      {enviando && envio ? (
        <BarraDeProgresso
          rotulo={
            progresso < 100
              ? "Carregando o lote…"
              : "Conferindo as imagens no servidor…"
          }
          percentual={progresso}
          indeterminada={progresso >= 100}
          detalhe={`${envio.quantos} imagem(ns) · ${tamanhoLegivel(envio.bytes)}`}
        />
      ) : (
        <>
          <Icone.Subir />
          <p>
            <b className="text-tinta-2">Arraste as imagens aqui</b>
            <br />
            <small>
              ou escolha no computador — várias de uma vez (_frente, _lateral,
              _traseira do mesmo veículo viram um registro)
            </small>
          </p>
        </>
      )}
      <Botao
        papel="primaria"
        disabled={travado}
        onClick={() => entrada.current?.click()}
      >
        {enviando ? "Carregando…" : "Escolher arquivos"}
      </Botao>
      <input
        ref={entrada}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp"
        hidden
        disabled={travado}
        aria-label="Escolher arquivos"
        onChange={(e) => void subir(e.target.files)}
      />
      {concluido && !enviando && (
        <p role="status" className="text-[11.5px] text-verde">
          {concluido}
        </p>
      )}
      {erro && (
        <p role="alert" className="text-[11.5px] text-vermelho">
          {erro}
        </p>
      )}
    </div>
  );
}

function GradeDeImagens({
  imagens,
  resultados,
  aoReprocessar,
  recortado,
}: {
  imagens: ImagemDoLote[];
  resultados: Record<string, ResultadoDaCapacidade[]>;
  aoReprocessar: () => Promise<void>;
  recortado: boolean;
}) {
  if (imagens.length === 0) {
    return (
      <Estado
        tipo="vazio"
        titulo={
          recortado
            ? "Nenhuma imagem neste recorte."
            : "Nenhuma imagem neste lote."
        }
        {...(recortado
          ? { detalhe: "Volte para “Imagens no lote” na régua acima." }
          : { detalhe: "Arraste os arquivos que o cliente mandou." })}
      />
    );
  }
  return (
    // `auto-fill` com piso de 240px, e não 180: a miniatura carrega o nome do arquivo, o
    // que quatro IAs leram e a frase do portão. A 180px o `overflow-hidden` do cartão corta
    // tudo isso em silêncio — e o que fica visível é "ocr placa" sem o que o OCR leu.
    <ul
      aria-label="Imagens do lote"
      className="grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]"
    >
      {imagens.map((imagem) => (
        // `min-w-0` na `li`: sem ele, a largura intrínseca da foto vira o mínimo da coluna,
        // a faixa estoura os 240px e o `overflow-hidden` do cartão corta o texto ao lado.
        <li key={imagem.id} className="min-w-0">
          <Miniatura
            imagem={imagem}
            resultados={resultados[imagem.id] ?? []}
            aoReprocessar={aoReprocessar}
          />
        </li>
      ))}
    </ul>
  );
}

const PORTAO = {
  liberado: { cor: "verde" as const, rotulo: "anônima" },
  "na-fila": { cor: "cinza" as const, rotulo: "na fila" },
  travado: { cor: "vermelho" as const, rotulo: "download travado" },
};

function Miniatura({
  imagem,
  resultados,
  aoReprocessar,
}: {
  imagem: ImagemDoLote;
  resultados: ResultadoDaCapacidade[];
  aoReprocessar: () => Promise<void>;
}) {
  const [reprocessando, definirReprocessando] = useState(false);
  // O original só para quem enviou o lote e para a supervisão (revisão de segurança de
  // 26/09): para os outros a rota responde 403, e a tela já abre na cópia com blur.
  const original = imagem.original_liberado;
  const [comBlur, definirComBlur] = useState(!original);
  const portao = portaoDaImagem(imagem);
  const falhouPorNossaCulpa = resultados.some(
    (r) => r.desfecho === "INDISPONIVEL",
  );

  async function reprocessar() {
    definirReprocessando(true);
    try {
      await api(`/analise/imagens/${imagem.id}/reprocessar`, {
        method: "POST",
        corpo: {},
      });
      await aoReprocessar();
    } finally {
      definirReprocessando(false);
    }
  }

  return (
    <figure className="grid content-start overflow-hidden rounded-cartao border border-linha bg-painel-2">
      {/* Altura fixa e recorte aqui, e não `aspect-video` com a imagem solta: a foto do
          pórtico chega em qualquer proporção, e deixá-la decidir a altura do cartão faria a
          grade ter cartões de alturas diferentes conforme o que o cliente mandou. */}
      {/* **Aqui o original aparece; na Operação, só o borrado** (Gisele, 21/09). Esta é a
          tela de quem enviou as fotos — o cliente precisa ver que há gente nelas para entender
          o que a anonimização faz. Com a cópia pronta, "ver com blur" mostra o depois ao lado
          do antes. Fora desta tela (Operação, SENATRAN, download), só a cópia anônima sai. */}
      <div
        data-tema="escuro"
        className="relative grid h-[132px] place-items-center overflow-hidden bg-mesa"
      >
        {comBlur && portao !== "liberado" ? (
          // Sem cópia anônima pronta e sem acesso ao original: nada a mostrar ainda.
          <span className="px-3 text-center text-[11px] text-tinta-3">
            {original
              ? "A cópia com blur ainda não existe."
              : "O original fica com quem enviou o lote; a cópia com blur aparece aqui quando ficar pronta."}
          </span>
        ) : (
          <img
            // A miniatura de 480 px (tarefa 20): o cartão tem 132 px de altura, e um lote de
            // trezentas fotos de celular baixava trezentos originais inteiros.
            src={`/analise/imagens/${imagem.id}/${comBlur ? "anonimizada" : "arquivo"}?largura=480`}
            alt={imagem.nome_original}
            loading="lazy"
            className="block h-full w-full object-contain"
          />
        )}
        <span className="absolute top-1.5 left-1.5 flex gap-1">
          <Badge cor={comBlur ? "verde" : "cinza"}>
            {comBlur ? "com blur" : "original"}
          </Badge>
        </span>
        {portao === "liberado" && original && (
          <button
            type="button"
            onClick={() => definirComBlur((v) => !v)}
            className="absolute right-1.5 bottom-1.5 rounded-botao border border-linha bg-mesa/85 px-2 py-0.5 text-[10.5px] font-semibold text-tinta-2 hover:text-tinta"
          >
            {comBlur ? "ver original" : "ver com blur"}
          </button>
        )}
      </div>

      <figcaption className="grid min-w-0 gap-1.5 p-2">
        <b className="truncate text-tinta" title={imagem.nome_original}>
          {imagem.nome_original}
        </b>
        <span className="text-[11px] text-tinta-3">
          {tamanhoLegivel(imagem.tamanho_bytes)} · {imagem.status.toLowerCase()}
        </span>

        {imagem.motivo_rejeicao && (
          <p className="text-[10.5px] wrap-anywhere text-vermelho">
            {imagem.motivo_rejeicao}
          </p>
        )}

        <Leituras resultados={resultados} />

        {/* A correção da `A-07`: o operador diz o que a IA não viu — e a categoria é o que
            dá preço ao trânsito que nasceu desta foto. */}
        {imagem.status === "CONCLUIDA" && <Correcoes imagemId={imagem.id} />}

        {/* O portão da `A-04`. Três estados, três frases — e o original nunca aparece como
            alternativa quando a cópia anônima não existe. */}
        <div className="grid min-w-0 justify-items-start gap-1 rounded-botao border border-linha bg-painel p-1.5">
          <Badge cor={PORTAO[portao].cor}>{PORTAO[portao].rotulo}</Badge>
          {portao === "liberado" ? (
            <a
              href={`/analise/imagens/${imagem.id}/download`}
              download
              className="inline-flex items-center gap-1 text-[11px] text-azul hover:underline"
            >
              <Icone.Baixar />
              baixar a cópia anônima
            </a>
          ) : portao === "na-fila" ? (
            <span className="text-[10.5px] text-tinta-3">
              o download libera depois da anonimização
            </span>
          ) : (
            <>
              <span className="text-[10.5px] wrap-anywhere text-tinta-3">
                {imagem.anonimizacao_observacao ??
                  "a anonimização não pôde ser garantida"}
              </span>
              <Botao
                disabled={reprocessando}
                onClick={() => void reprocessar()}
              >
                {reprocessando ? "reprocessando…" : "Reprocessar"}
              </Botao>
              {/* O ADR-012 decide a cobrança no servidor; a tela só conta ao operador o que
                  a decisão vai ser. Um botão que às vezes custa e às vezes não, sem dizer
                  qual dos dois é agora, é o que faz o operador parar de apertar. */}
              <span className="text-[10px] wrap-anywhere text-tinta-3">
                {falhouPorNossaCulpa
                  ? "sem nova cobrança: a falha foi nossa"
                  : "roda as capacidades de novo e é cobrado"}
              </span>
            </>
          )}
        </div>
      </figcaption>
    </figure>
  );
}

const CAMPOS: { valor: CampoCorrigivel; rotulo: string; dica: string }[] = [
  {
    valor: "categoria",
    rotulo: "Categoria tarifária",
    dica: "1 a 7, ou 8 para Especial",
  },
  { valor: "placa", rotulo: "Placa", dica: "ABC1D23 ou ABC1234" },
  {
    valor: "classe",
    rotulo: "Classe do veículo",
    dica: "passeio (carro, moto) ou comercial",
  },
];

/**
 * A correção do operador (`A-07`), no próprio cartão da imagem.
 *
 * **Nada é sobrescrito.** A leitura da IA continua nas linhas acima; a correção entra numa
 * lista ao lado, com quem e por quê — e a justificativa é obrigatória no banco, não só aqui.
 * A categoria é a que importa para a demonstração: nenhuma IA daqui classifica veículo
 * ainda, então é o operador que dá ao trânsito o número que a tarifa lê.
 *
 * A lista de correções só é buscada quando o operador abre o bloco: uma requisição por
 * miniatura transformaria um lote de vinte fotos em vinte chamadas na carga da tela.
 */
function Correcoes({ imagemId }: { imagemId: string }) {
  const [aberto, definirAberto] = useState(false);
  const [historico, definirHistorico] = useState<CorrecaoFeita[] | null>(null);
  const [campo, definirCampo] = useState<CampoCorrigivel>("categoria");
  const [valor, definirValor] = useState("");
  const [justificativa, definirJustificativa] = useState("");
  const [salvando, definirSalvando] = useState(false);
  const [recusa, definirRecusa] = useState<string | null>(null);

  async function carregar() {
    definirHistorico(
      await api<CorrecaoFeita[]>(`/analise/imagens/${imagemId}/correcoes`),
    );
  }

  async function abrir() {
    definirAberto((a) => !a);
    if (historico === null) await carregar();
  }

  async function salvar(evento: FormEvent) {
    evento.preventDefault();
    definirRecusa(null);
    definirSalvando(true);
    try {
      await api(`/analise/imagens/${imagemId}/correcoes`, {
        method: "POST",
        corpo: { campo, valor_novo: valor, justificativa },
      });
      definirValor("");
      definirJustificativa("");
      await carregar();
    } catch (falha) {
      definirRecusa(
        falha instanceof ErroDaApi
          ? falha.message
          : "Não foi possível corrigir.",
      );
    } finally {
      definirSalvando(false);
    }
  }

  const vigente = (c: CampoCorrigivel) =>
    historico?.find((h) => h.campo === c)?.valor_novo;
  const escolhido = CAMPOS.find((c) => c.valor === campo)!;

  return (
    <div className="grid min-w-0 gap-1.5 rounded-botao border border-linha bg-painel p-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => void abrir()}
          aria-expanded={aberto}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-azul hover:underline"
        >
          <Icone.Seta />
          {aberto ? "Fechar correção" : "Corrigir leitura"}
        </button>
        {historico && historico.length > 0 && (
          <span className="mono text-[10.5px] text-tinta-2">
            {(["categoria", "placa", "classe"] as CampoCorrigivel[])
              .filter((c) => vigente(c))
              .map((c) => `${c} ${vigente(c)}`)
              .join(" · ")}
          </span>
        )}
      </div>

      {aberto && (
        <>
          <form
            aria-label="Corrigir leitura"
            onSubmit={(e) => void salvar(e)}
            className="grid gap-1.5"
          >
            <label className="grid gap-0.5 text-[10.5px] text-tinta-3">
              Campo
              <select
                value={campo}
                aria-label="Campo"
                onChange={(e) =>
                  definirCampo(e.target.value as CampoCorrigivel)
                }
                className="min-h-8 rounded-botao border border-linha bg-painel-2 px-2 text-[11.5px] text-tinta-2"
              >
                {CAMPOS.map((c) => (
                  <option key={c.valor} value={c.valor}>
                    {c.rotulo}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-0.5 text-[10.5px] text-tinta-3">
              Valor correto ({escolhido.dica})
              <input
                value={valor}
                required
                aria-label="Valor correto"
                onChange={(e) => definirValor(e.target.value)}
                className="mono min-h-8 rounded-botao border border-linha bg-painel-2 px-2 text-[11.5px] text-tinta"
              />
            </label>
            <label className="grid gap-0.5 text-[10.5px] text-tinta-3">
              Justificativa (obrigatória)
              <input
                value={justificativa}
                required
                minLength={5}
                aria-label="Justificativa"
                placeholder="O que você viu na foto que a IA não viu"
                onChange={(e) => definirJustificativa(e.target.value)}
                className="min-h-8 rounded-botao border border-linha bg-painel-2 px-2 text-[11.5px] text-tinta"
              />
            </label>
            <Botao
              papel="primaria"
              type="submit"
              disabled={
                salvando || justificativa.trim().length < 5 || !valor.trim()
              }
            >
              {salvando ? "salvando…" : "Salvar correção"}
            </Botao>
            {recusa && (
              <p
                role="alert"
                className="text-[10.5px] wrap-anywhere text-vermelho"
              >
                {recusa}
              </p>
            )}
          </form>

          {historico && historico.length > 0 && (
            <ul
              aria-label="Correções feitas"
              className="grid gap-1 border-t border-linha-suave pt-1.5"
            >
              {historico.map((c) => (
                <li key={c.id} className="text-[10.5px] text-tinta-3">
                  <span className="mono text-tinta-2">
                    {c.campo} {c.valor_anterior ?? "—"} → {c.valor_novo}
                  </span>{" "}
                  · {c.usuario_nome} · {dataEHora(c.em)}
                  <br />
                  <span className="wrap-anywhere">{c.justificativa}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

/**
 * O que cada IA disse, em uma linha por capacidade.
 *
 * Os três pares que a `A-05` proíbe colapsar aparecem aqui: *placa ilegível* (o modelo olhou
 * e não leu) é diferente de *não verificado* (o modelo não rodou); *eixos não se aplicam* é
 * diferente de *zero eixos*; e uma falha nossa (`INDISPONIVEL`) é diferente de um julgamento
 * sobre a imagem (`ENTRADA_INADEQUADA`).
 */
function Leituras({ resultados }: { resultados: ResultadoDaCapacidade[] }) {
  // A anonimização fica **fora** desta lista: ela não é uma leitura sobre o veículo, é o
  // portão da saída — e o bloco logo abaixo já a conta com os três estados e com a frase do
  // motivo. Repetida aqui, ela apareceria como um "sem garantia" seco ao lado de um
  // "ANÔNIMA" verde, e as duas frases sobre o mesmo fato se contradizendo na mesma tela é
  // pior do que qualquer uma delas sozinha.
  const leituras = resultados.filter((r) => r.capacidade !== "anonimizacao");
  if (leituras.length === 0) {
    return (
      <span className="text-[10.5px] text-tinta-3">ainda não analisada</span>
    );
  }
  return (
    <ul className="grid gap-0.5">
      {leituras.map((r) => {
        const { texto, falhou } = resumoDaCapacidade(r);
        return (
          <li
            key={r.capacidade}
            className="flex flex-wrap items-baseline justify-between gap-x-1.5 text-[11px]"
          >
            {/* `flex-wrap` e `min-w-0`: a leitura pode ser "eixos não se aplicam" ao lado de
                "contagem eixos", e num cartão de 240px isso não cabe numa linha. Quebrar
                perde densidade; cortar perde justamente o que o cliente veio ver. */}
            <span className="text-tinta-3">
              {r.capacidade.replace(/_/g, " ")}
            </span>
            <span
              className={`min-w-0 wrap-anywhere ${falhou ? "text-vermelho" : "mono text-tinta-2"}`}
            >
              {texto}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
